import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ArrowSquareOut, Calendar, User, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { PROJECT_ESTADOS, EJES_ESTRATEGICOS } from "../lib/constants";
import { formatDate, formatDateTime, formatLabel } from "../lib/format";
import RichEditor, { RichView } from "../components/RichEditor";
import { LinksEditor, EvidenceUploader, TabLabel } from "../components/DetailHelpers";
import MoneyInput from "../components/MoneyInput";
import ReminderBar from "../components/ReminderBar";
import { useConfirm } from "../components/ConfirmDialog";


export default function ProjectDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updNew, setUpdNew] = useState({ fecha: "", descripcion: "", percent_avance: "", estado: "" });

  const load = async () => {
    const r = await api.get(`/projects/${id}`);
    setData(r.data);
    const p = r.data.project;
    setForm({
      name: p.name || "",
      description: p.description || "",
      eje_estrategico: p.eje_estrategico || "",
      tipo: p.tipo || "",
      related_entity_type: p.related_entity_type || "",
      related_entity_id: p.related_entity_id || "",
      fecha_inicio: p.fecha_inicio || "",
      fecha_termino: p.fecha_termino || "",
      estado: p.estado || "planificacion",
      responsable: p.responsable || "",
      responsable_email: p.responsable_email || "",
      organizaciones_involucradas: p.organizaciones_involucradas || "",
      presupuesto: p.presupuesto || 0,
      inversion_ejecutada: p.inversion_ejecutada || 0,
      beneficiarios_directos: p.beneficiarios_directos || 0,
      beneficiarios_indirectos: p.beneficiarios_indirectos || 0,
      indicadores_impacto: p.indicadores_impacto || [],
      percent_avance: p.percent_avance || 0,
      assigned_to: p.assigned_to || null,
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!data || !form) return <div className="p-8 text-slate-500">Cargando…</div>;
  const p = data.project;

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...form };
      ["fecha_inicio", "fecha_termino", "responsable_email",
       "related_entity_type", "related_entity_id"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.put(`/projects/${id}`, payload);
      toast.success("Proyecto actualizado");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "Error al guardar";
      toast.error(msg);
      throw e;
    } finally { setSaving(false); }
  };

  const addUpdate = async () => {
    if (!updNew.descripcion || updNew.descripcion === "<p><br></p>") return toast.error("Describe el avance");
    try {
      await api.post(`/projects/${id}/updates`, {
        fecha: updNew.fecha || null,
        descripcion: updNew.descripcion,
        percent_avance: updNew.percent_avance === "" ? null : Number(updNew.percent_avance),
        estado: updNew.estado || null,
      });
      toast.success("Avance registrado");
      setUpdNew({ fecha: "", descripcion: "", percent_avance: "", estado: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar avance");
    }
  };

  const delUpdate = async (uid) => {
    if (!(await confirm({ title: "Eliminar avance", description: "¿Deseas eliminar este avance del proyecto?", danger: true }))) return;
    await api.delete(`/progress_updates/${uid}`);
    await load();
  };

  const pctEjec = p.presupuesto > 0 ? Math.round((p.inversion_ejecutada / p.presupuesto) * 100) : 0;
  const formPctEjec = form.presupuesto > 0 ? Math.round((form.inversion_ejecutada / form.presupuesto) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={p.name}
        description={
          <div className="flex flex-wrap items-center gap-2">
            {p.eje_estrategico && <StatusBadge tone="info">{p.eje_estrategico}</StatusBadge>}
            <EstadoBadge value={p.estado} />
            {p.fecha_inicio && <span className="text-xs text-slate-500"><Calendar size={12} className="inline mr-1" />{formatDate(p.fecha_inicio)}{p.fecha_termino && ` → ${formatDate(p.fecha_termino)}`}</span>}
            {(p.responsable || p.responsable_email) && (
              <span className="text-xs text-slate-600 inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
                <User size={12} />
                <span className="font-medium">{p.responsable || "Sin nombre"}</span>
                {p.responsable_email && <span className="text-slate-500">· {p.responsable_email}</span>}
              </span>
            )}
          </div>
        }
      >
        <Button onClick={save} disabled={saving} className="bg-brand-green-700 hover:bg-brand-green-800 text-white" data-testid="header-save-btn">
          <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
        <Button variant="secondary" onClick={() => nav(-1)}>← Volver</Button>
      </PageHeader>

      <div className="px-6 space-y-6">
        {/* Reminder bar */}
        <ReminderBar
          entityKind="projects"
          entityId={id}
          form={form}
          setForm={setForm}
          startKey="fecha_inicio"
          endKey="fecha_termino"
          onSaveNeeded={save}
        />

        {/* Descripción del proyecto */}
        {p.description && (
          <Card className="rounded-md border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Descripción del proyecto</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{p.description}</div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Avance operativo" value={`${p.percent_avance || 0}%`} accent="emerald" />
          <KPI label="Avance financiero" value={`${pctEjec}%`} accent="amber" />
          <KPI label="Presupuesto" value={p.presupuesto ? `$${Number(p.presupuesto).toLocaleString("es-CL")}` : "—"} />
          <KPI label="Ejecutado" value={`$${Number(p.inversion_ejecutada || 0).toLocaleString("es-CL")}`} />
          <KPI label="Beneficiarios" value={(p.beneficiarios_directos || 0) + (p.beneficiarios_indirectos || 0)} />
        </div>

        <Card className="rounded-md">
          <CardContent className="p-4 space-y-5">
            {/* AVANCE OPERATIVO (manual) */}
            <div>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-semibold text-slate-700">Avance operativo (físico / hitos)</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.percent_avance}
                    onChange={(e) => setForm({ ...form, percent_avance: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                    className="h-7 w-20 text-right"
                  />
                  <span className="font-semibold text-slate-900 w-6 text-right">%</span>
                </div>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={form.percent_avance}
                onChange={(e) => setForm({ ...form, percent_avance: Number(e.target.value) })}
                className="w-full accent-emerald-500"
                data-testid="proj-progress"
              />
              <p className="text-[11px] text-slate-500 mt-1">Mide cuánto del trabajo / hitos del proyecto se han completado.</p>
            </div>

            {/* AVANCE FINANCIERO (calculado) */}
            <div>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-semibold text-slate-700">Avance financiero (entrega de recursos)</span>
                <span className="font-semibold text-amber-700">{formPctEjec}%</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, formPctEjec)}%` }} />
              </div>
              <div className="flex items-center justify-between gap-4 mt-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Presupuesto</Label>
                  <MoneyInput
                    value={form.presupuesto}
                    onChange={(n) => setForm({ ...form, presupuesto: n })}
                    className="h-8"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Ejecutado</Label>
                  <MoneyInput
                    value={form.inversion_ejecutada}
                    onChange={(n) => setForm({ ...form, inversion_ejecutada: n })}
                    className="h-8"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Se calcula automáticamente como <strong>Ejecutado / Presupuesto</strong>.</p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_ESTADOS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="bg-slate-900" onClick={save} disabled={saving}>
                <FloppyDisk size={14} className="mr-1" /> Guardar cambios
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="seguimiento">
          <TabsList>
            <TabsTrigger value="seguimiento"><TabLabel label={`Seguimiento (${data.updates.length})`} hasData={data.updates.length > 0} /></TabsTrigger>
            <TabsTrigger value="detalle"><TabLabel label="Detalles" hasData /></TabsTrigger>
            <TabsTrigger value="compromisos"><TabLabel label={`Compromisos (${data.commitments.length})`} hasData={data.commitments.length > 0} /></TabsTrigger>
            <TabsTrigger value="vinculos"><TabLabel label="Vinculaciones" hasData={!!data.related} /></TabsTrigger>
            <TabsTrigger value="evidencias"><TabLabel label={`Evidencias (${data.documents.length})`} hasData={data.documents.length > 0} /></TabsTrigger>
          </TabsList>

          <TabsContent value="seguimiento">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Registrar nuevo avance</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Fecha</Label><Input type="date" value={updNew.fecha} onChange={(e) => setUpdNew({ ...updNew, fecha: e.target.value })} /></div>
                  <div><Label>% Avance</Label><Input type="number" min="0" max="100" value={updNew.percent_avance} onChange={(e) => setUpdNew({ ...updNew, percent_avance: e.target.value })} placeholder="opcional" /></div>
                  <div>
                    <Label>Cambiar estado a</Label>
                    <Select value={updNew.estado || "__none__"} onValueChange={(v) => setUpdNew({ ...updNew, estado: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="No cambiar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No cambiar</SelectItem>
                        {PROJECT_ESTADOS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Descripción del avance</Label><RichEditor value={updNew.descripcion} onChange={(html) => setUpdNew({ ...updNew, descripcion: html })} placeholder="Hito alcanzado, decisiones, próximos pasos…" /></div>
                <div className="text-right"><Button onClick={addUpdate} className="bg-slate-900"><Plus size={14} className="mr-1" /> Registrar avance</Button></div>
              </CardContent>
            </Card>

            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Bitácora de seguimiento</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {data.updates.length === 0 && <div className="text-sm text-slate-500 italic">Sin avances registrados todavía.</div>}
                {data.updates.map((u) => (
                  <div key={u.id} className="border-l-2 border-brand-green-400 pl-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500">
                        <Calendar size={12} className="inline mr-1" />{formatDate(u.fecha)}
                        {u.author_name && <span className="ml-2"><User size={12} className="inline mr-0.5" />{u.author_name}</span>}
                        <span className="ml-3 text-[10px] text-slate-400">{formatDateTime(u.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.percent_avance !== null && u.percent_avance !== undefined && <StatusBadge tone="success">{u.percent_avance}%</StatusBadge>}
                        {u.estado && <EstadoBadge value={u.estado} />}
                        <Button size="sm" variant="ghost" onClick={() => delUpdate(u.id)}><Trash size={12} className="text-red-600" /></Button>
                      </div>
                    </div>
                    <RichView html={u.descripcion} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="detalle">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Información editable</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-span-2"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <Label>Eje estratégico</Label>
                  <Select value={form.eje_estrategico || "__none__"} onValueChange={(v) => setForm({ ...form, eje_estrategico: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {EJES_ESTRATEGICOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tipo</Label><Input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} placeholder="Ej: compensacion, voluntario, infraestructura" /></div>
                <div><Label>Fecha inicio</Label><Input type="date" value={form.fecha_inicio || ""} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value || null })} /></div>
                <div><Label>Fecha término</Label><Input type="date" value={form.fecha_termino || ""} onChange={(e) => setForm({ ...form, fecha_termino: e.target.value || null })} /></div>
                <div><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} /></div>
                <div><Label>Email(s) responsable — separa con coma para varios</Label><Input type="text" value={form.responsable_email} onChange={(e) => setForm({ ...form, responsable_email: e.target.value })} placeholder="nombre@empresa.cl, otro@empresa.cl" /></div>
                <div className="col-span-2"><Label>Organizaciones involucradas</Label><Input value={form.organizaciones_involucradas} onChange={(e) => setForm({ ...form, organizaciones_involucradas: e.target.value })} /></div>
                <div><Label>Presupuesto (CLP)</Label><MoneyInput value={form.presupuesto} onChange={(n) => setForm({ ...form, presupuesto: n })} /></div>
                <div><Label>Inversión ejecutada (CLP)</Label><MoneyInput value={form.inversion_ejecutada} onChange={(n) => setForm({ ...form, inversion_ejecutada: n })} /></div>
                <div><Label>% Avance operativo</Label><Input type="number" min="0" max="100" value={form.percent_avance} onChange={(e) => setForm({ ...form, percent_avance: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} /></div>
                <div></div>
                <div><Label>Beneficiarios directos</Label><Input type="number" value={form.beneficiarios_directos} onChange={(e) => setForm({ ...form, beneficiarios_directos: Number(e.target.value) })} /></div>
                <div><Label>Beneficiarios indirectos</Label><Input type="number" value={form.beneficiarios_indirectos} onChange={(e) => setForm({ ...form, beneficiarios_indirectos: Number(e.target.value) })} /></div>
                <div className="col-span-2 text-right">
                  <Button onClick={save} disabled={saving} className="bg-slate-900"><FloppyDisk size={14} className="mr-1" /> Guardar</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="compromisos">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Compromisos asociados al proyecto</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Compromiso</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead>Avance</TableHead><TableHead>Semáforo</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.commitments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Sin compromisos asociados.</TableCell></TableRow>}
                    {data.commitments.map((c) => (
                      <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/compromisos/${c.id}`)}>
                        <TableCell className="font-medium"><Link to={`/compromisos/${c.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{c.name}</Link></TableCell>
                        <TableCell><StatusBadge tone="info">{c.tipo}</StatusBadge></TableCell>
                        <TableCell><EstadoBadge value={c.estado} /></TableCell>
                        <TableCell>{formatDate(c.fecha_compromiso)}</TableCell>
                        <TableCell>{c.percent_avance || 0}%</TableCell>
                        <TableCell><Semaforo value={c.semaforo} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vinculos">
            <LinksEditor
              related={data.related}
              onChange={async (patch) => {
                const updated = { ...form, ...patch };
                setForm(updated);
                await api.put(`/projects/${id}`, updated);
                await load();
              }}
            />
          </TabsContent>

          <TabsContent value="evidencias">
            <EvidenceUploader
              entityType="project"
              entityId={id}
              documents={data.documents}
              onReload={load}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function KPI({ label, value, accent }) {
  const color = accent === "emerald" ? "text-brand-green-700"
              : accent === "amber" ? "text-amber-700"
              : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
